import { Button, Form, Input, Select, Spin, Typography } from 'antd'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import routes from '@/constants/routes'
import { Detergent, Tank } from '@/types'
import { GetDetergentResponse } from '@/types/apis/detergents/GetDetergentResponse'
import { UpdateDetergentRequest } from '@/types/apis/detergents/UpdateDetergentRequest'

import { SpaceCustom } from '../../style'

type Props = {
  detergentId: number
  updateLoading: boolean
  getDetergentResponse: GetDetergentResponse | undefined
  getLoading: boolean
  detergentData?: { name: string; unit: string; remainingQuantity: number }
  tanks?: Tank[]
  onSubmit: (formValues: UpdateDetergentRequest) => void
  onDelete: (id: Detergent['id']) => void
}

const { Title } = Typography

const UpdateDetergentForm = ({ updateLoading, getLoading, getDetergentResponse, onSubmit, tanks }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm<UpdateDetergentRequest>()

  useEffect(() => {
    if (getDetergentResponse) {
      form.setFieldsValue(getDetergentResponse)
      const tankIds = getDetergentResponse.tanks.map((tank) => tank.id)
      form.setFieldsValue({ ...getDetergentResponse, tankIds })
    }
  }, [getDetergentResponse, form])

  return (
    <Spin spinning={getLoading}>
      <Form layout="vertical" onFinish={onSubmit} form={form}>
        <Form.Item
          label={t('components.pages.detergents.create.form.name')}
          name="name"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requireName') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={t('components.pages.detergents.create.form.price')}
          name="price"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requirePrice') }]}
        >
          <Input type="number" step={0.1} min={0} suffix="円" />
        </Form.Item>

        <Form.Item
          label={t('components.pages.detergents.create.form.netWeightG')}
          name="netWeightG"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requireNetWeightG') }]}
        >
          <Input type="number" min={0} suffix="g" />
        </Form.Item>

        <Form.Item
          label={t('components.pages.detergents.create.form.netWeightMl')}
          name="netWeightMl"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requireNetWeightMl') }]}
        >
          <Input type="number" min={0} suffix="ml" />
        </Form.Item>

        <Form.Item
          label={t('components.pages.detergents.create.form.specificGravity')}
          name="specificGravity"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requireSpecificGravity') }]}
        >
          <Input type="number" step={0.001} min={0} />
        </Form.Item>

        <Form.Item
          label={t('components.pages.detergents.create.form.costPerMl')}
          name="costPerMl"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requireCostPerMl') }]}
        >
          <Input type="number" step={0.00001} min={0} suffix="円" />
        </Form.Item>

        <Form.Item
          label={t('components.pages.detergents.create.form.thresholdQuantity')}
          name="thresholdQuantity"
          rules={[{ required: true, message: t('components.pages.detergents.create.form.requireThresholdQuantity') }]}
        >
          <Input type="number" min={0} suffix="ケース" />
        </Form.Item>

        <Title level={4}>{t('components.pages.detergents.create.form.tankInfo')}</Title>
        <Form.Item label={t('components.pages.detergents.create.form.tankNo')} name="tankIds">
          <Select mode="multiple" style={{ border: '1px solid #ccc' }}>
            {tanks?.map((tank) => (
              <Select.Option key={tank.id} value={tank.id}>
                {tank.tankNo}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <SpaceCustom>
            <Button
              onClick={() => {
                navigate(routes.detergents.path)
              }}
            >
              {t('components.pages.detergents.update.form.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={updateLoading}>
              {t('components.pages.detergents.update.form.update')}
            </Button>
          </SpaceCustom>
        </Form.Item>
      </Form>
    </Spin>
  )
}

UpdateDetergentForm.displayName = 'UpdateDetergentForm'
export default UpdateDetergentForm
